<template>
  <div>
    <v-label>{{ $m.filter_conditions }}</v-label>
    <bm-dropdown
      :value.sync="filterState.filter1.type"
      class="filter-type slim-control"
      :items="filterTypes"
      :item-text="extractFilterTypeName"
      item-value="type"
      :searchable="false"
      :sort-items="false"
    >
    </bm-dropdown>
    <slot
      name="control1"
      :column-config="columnConfig"
      :filter-type="filterState.filter1.type"
      :value="filterState.filter1.value"
      :set-value="setFilter1Value"
    >
      <bm-text
        :value.sync="filterState.filter1.value"
        class="slim-control"
        :placeholder="$m.filter_value"
      >
      </bm-text>
    </slot>
    <template v-if="useTwoFilters && canUseSecondFilter && filterState.filter2">
      <bm-radio-group
        :value.sync="filterState.filter2.operator"
        class="slim-control"
        row
      >
        <bm-radio :label="$m.filter_and" :value="'and'"></bm-radio>
        <bm-radio :label="$m.filter_or" :value="'or'"></bm-radio>
      </bm-radio-group>
      <bm-dropdown
        :value.sync="filterState.filter2.type"
        class="filter-type slim-control"
        :items="filterTypes"
        :item-text="extractFilterTypeName"
        item-value="type"
        :searchable="false"
        :sort-items="false"
      >
      </bm-dropdown>
      <slot
        name="control2"
        :column-config="columnConfig"
        :value="filterState.filter2.value"
        :set-value="setFilter2Value"
      >
        <bm-text
          :value.sync="filterState.filter2.value"
          class="slim-control"
          :placeholder="$m.filter_value"
        >
        </bm-text>
      </slot>
    </template>
  </div>
</template>

<script setup lang="ts">
import type {
  BmGridColumnConfiguration,
  BmGridState,
} from "@/components/controls/grid/BmGridData";
import type {
  FilterState,
  FilterType,
  FilterValue,
} from "@/components/controls/grid/columnfilters/ColumnFilters";
import BmRadioGroup from "@/components/controls/input/BmRadioGroup.vue";
import type BaseEnumType from "@/app/services/baseEnumType";
import { ref, computed, watch, nextTick } from "vue";
import { makeDeepRef } from "@/components/composables/componentTools";
import { translate } from "@/plugins/i18n";
import Key from "@/app/types/common/key";

interface ColumnFilterComponentProps {
  columnKey: string;
  gridState: BmGridState;
  columnConfig: BmGridColumnConfiguration;
  filterTypes: FilterType[];
  useTwoFilters?: boolean;
}

const props = withDefaults(defineProps<ColumnFilterComponentProps>(), {
  useTwoFilters: true,
});

const resettingState = ref(false);

// Use a return function so we don't run into obj ref issues
const buildDefaultFilterState = () => ({
  filter1: {
    type: props.filterTypes
      ? props.filterTypes[0].type.toString()
      : "filter_show_all",
    value: "",
  },
  filter2: {
    type: props.filterTypes
      ? props.filterTypes[0].type.toString()
      : "filter_show_all",
    value: "",
    operator: "and",
  },
});

const filterState = makeDeepRef<FilterState>(
  buildDefaultFilterState() as FilterState
);
const extractFilterTypeName = (item: FilterType) =>
  translate(Key.fromString(item.type));

function setFilter1Value(val: FilterValue) {
  filterState.value.filter1.value = val;
}

function setFilter2Value(val: FilterValue) {
  if (!filterState.value.filter2) {
    return;
  }

  filterState.value.filter2.value = val;
}

const canUseSecondFilter = computed((): boolean => {
  if (
    filterState.value.filter1.value === "" ||
    filterState.value.filter1.value === null
  ) {
    return false;
  }

  // If we want to show all
  if (filterState.value.filter1.type === "filter_show_all") {
    return false;
  }

  if (filterState.value.filter1.value instanceof String) {
    return filterState.value.filter1.value.length > 0;
  }

  if (filterState.value.filter1.value instanceof Number) {
    return true;
  }

  return (filterState.value.filter1.value as BaseEnumType).value !== "None";
});

watch(
  props.gridState,
  () => {
    const currentState = props.gridState.columnFilters[props.columnKey];

    if (currentState === undefined) {
      resettingState.value = true;
      // Deep clone so we don't overwrite values in our default obj
      filterState.value = buildDefaultFilterState() as FilterState;

      // The watcher fires after we've set this back to false, so wait for vue to process
      // We must set to value here not in filterState watcherm otherwise first load = undefined,
      // we would set resettingState to true, and ignore the first input of a clean filter
      nextTick(() => {
        resettingState.value = false;
      });
      return;
    }

    // Load state if it seems valid
    if (
      currentState &&
      currentState.filter1 &&
      currentState.filter1.type &&
      currentState.filter2 &&
      currentState.filter2.type &&
      currentState.filter2.operator
    ) {
      filterState.value = currentState;
    }
  },
  { immediate: true }
);

watch(
  filterState,
  () => {
    // We want to set our internal state back to default without causing an update
    if (resettingState.value) {
      return;
    }

    props.gridState.setFilter(props.columnKey, filterState.value);
  },
  { deep: true }
);
</script>

<style scoped lang="sass">
div
  margin-top: 10px

.filter-type.bm-field
  margin-bottom: 0
</style>
